import { useState, useEffect, useCallback } from 'react';
import { PARODY_SITES } from './parody-data.js';
import { useWindowManagerContext } from '../contexts/WindowManagerContext';
import PortalPage from './sites/PortalPage';
import YouToobPage from './sites/YouToobPage';
import MyFacePage from './sites/MyFacePage';
import WinampGalaxyPage from './sites/WinampGalaxyPage';
import MegaFunZonePage from './sites/MegaFunZonePage';
import RandomSitePage from './sites/RandomSitePage';

export const InternetExplorer = ({
  windowId,
  initialUrl = 'portal',
  playSound,
  initialYouToobVideoId = null,
  initialYouToobAutoplay = false,
}) => {
  const { openWindow, updateWindowTitle } = useWindowManagerContext();
  const initialSiteState = initialUrl === 'youtoob'
    ? {
        initialVideoId: initialYouToobVideoId,
        autoPlayInitial: initialYouToobAutoplay,
      }
    : null;
  const [currentSite, setCurrentSite] = useState(initialUrl);
  const [currentSiteState, setCurrentSiteState] = useState(initialSiteState);
  const [history, setHistory] = useState([{ siteKey: initialUrl, state: initialSiteState }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [addressBarValue, setAddressBarValue] = useState('');

  // Update address bar and window title when site changes
  useEffect(() => {
    const site = PARODY_SITES[currentSite];
    if (site) {
      setAddressBarValue(site.url);
      if (windowId && updateWindowTitle) {
        updateWindowTitle(windowId, site.name);
      }
    }
  }, [currentSite]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateTo = useCallback((siteKey, siteState = null) => {
    if (playSound) playSound('click', 0.2);

    if (!PARODY_SITES[siteKey]) return;

    const nextEntry = { siteKey, state: siteState };
    setCurrentSite(siteKey);
    setCurrentSiteState(siteState);
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(nextEntry);
      return newHistory;
    });
    setHistoryIndex((prev) => prev + 1);
  }, [playSound, historyIndex]);

  const goBack = () => {
    if (historyIndex > 0) {
      if (playSound) playSound('click', 0.2);
      const nextIndex = historyIndex - 1;
      const previousEntry = history[nextIndex];
      setHistoryIndex(nextIndex);
      setCurrentSite(previousEntry.siteKey);
      setCurrentSiteState(previousEntry.state || null);
    }
  };

  const goForward = () => {
    if (historyIndex < history.length - 1) {
      if (playSound) playSound('click', 0.2);
      const nextIndex = historyIndex + 1;
      const forwardEntry = history[nextIndex];
      setHistoryIndex(nextIndex);
      setCurrentSite(forwardEntry.siteKey);
      setCurrentSiteState(forwardEntry.state || null);
    }
  };

  const goHome = () => {
    navigateTo('portal');
  };

  const refresh = () => {
    if (playSound) playSound('click', 0.2);
    // Force re-render
    const current = currentSite;
    setCurrentSite('');
    setTimeout(() => setCurrentSite(current), 100);
  };

  const stopLoading = () => {
    if (playSound) playSound('click', 0.2);
  };

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    // Try to match URL to a site
    const siteKey = Object.keys(PARODY_SITES).find(
      key => PARODY_SITES[key].url.toLowerCase().includes(addressBarValue.toLowerCase().replace('http://', '').replace('www.', ''))
    );
    if (siteKey) {
      navigateTo(siteKey);
    }
  };

  const renderCurrentSite = () => {
    switch (currentSite) {
      case 'portal':
        return <PortalPage onNavigate={navigateTo} />;
      case 'youtoob':
        return (
          <YouToobPage
            onNavigate={navigateTo}
            onOpenPopup={(videoId, title) => openWindow('youtube', { videoId, title })}
            initialVideoId={currentSiteState?.initialVideoId ?? null}
            autoPlayInitial={currentSiteState?.autoPlayInitial ?? false}
          />
        );
      case 'myface':
        return <MyFacePage onNavigate={navigateTo} />;
      case 'winampgalaxy':
        return <WinampGalaxyPage onNavigate={navigateTo} />;
      case 'megafunzone':
        return <MegaFunZonePage onNavigate={navigateTo} onOpenGame={(swfUrl, gameName) => openWindow('ruffle', { swfUrl, gameName })} />;
      case 'randomsite':
        return <RandomSitePage onNavigate={navigateTo} />;
      default:
        return <PortalPage onNavigate={navigateTo} />;
    }
  };

  return (
    <div className="ie-browser" data-testid="ie-browser">
      {/* IE Toolbar */}
      <div className="ie-toolbar">
        <div className="ie-menu-bar">
          <span>File</span>
          <span>Edit</span>
          <span>View</span>
          <span>Favorites</span>
          <span>History</span>
          <span>Tools</span>
          <span>Help</span>
        </div>
        <div className="ie-nav-bar ie-toolbar-row ie-toolbar-primary">
          <div className="ie-nav-buttons">
            <button 
              className={`ie-btn ${historyIndex <= 0 ? 'disabled' : ''}`} 
              onClick={goBack}
              disabled={historyIndex <= 0}
              data-testid="ie-back-btn" 
              aria-label="Back"
            >
              <span className="ie-btn-glyph">◀</span>
              <span className="ie-btn-label">Back</span>
            </button>
            <button 
              className={`ie-btn ${historyIndex >= history.length - 1 ? 'disabled' : ''}`}
              onClick={goForward}
              disabled={historyIndex >= history.length - 1}
              data-testid="ie-forward-btn" 
              aria-label="Forward"
            >
              <span className="ie-btn-glyph">▶</span>
              <span className="ie-btn-label">Forward</span>
            </button>
            <button className="ie-btn" onClick={stopLoading} aria-label="Stop">
              <span className="ie-btn-glyph">X</span>
              <span className="ie-btn-label">Stop</span>
            </button>
            <button className="ie-btn" onClick={refresh} data-testid="ie-refresh-btn" aria-label="Refresh">
              <span className="ie-btn-glyph">R</span>
              <span className="ie-btn-label">Refresh</span>
            </button>
            <button className="ie-btn" onClick={goHome} data-testid="ie-home-btn" aria-label="Home">
              <span className="ie-btn-glyph">H</span>
              <span className="ie-btn-label">Home</span>
            </button>
            <button className="ie-btn" aria-label="Favorites">
              <span className="ie-btn-glyph">F</span>
              <span className="ie-btn-label">Favorites</span>
            </button>
            <button className="ie-btn" aria-label="History">
              <span className="ie-btn-glyph">Y</span>
              <span className="ie-btn-label">History</span>
            </button>
            <button className="ie-btn" aria-label="Mail">
              <span className="ie-btn-glyph">@</span>
              <span className="ie-btn-label">Mail</span>
            </button>
            <button className="ie-btn" aria-label="Print">
              <span className="ie-btn-glyph">P</span>
              <span className="ie-btn-label">Print</span>
            </button>
          </div>
          <div className="ie-nav-divider" />
          <form className="ie-address-bar" onSubmit={handleAddressSubmit}>
            <span className="address-label">Address:</span>
            <input
              type="text"
              value={addressBarValue}
              onChange={(e) => setAddressBarValue(e.target.value)}
              className="address-input"
              data-testid="ie-address-input"
            />
            <button type="submit" className="ie-go-btn" data-testid="ie-go-btn">Go</button>
          </form>
        </div>
        <div className="ie-links-bar">
          <span className="ie-links-label">Links</span>
          <a href="#" onClick={(e) => { e.preventDefault(); navigateTo('portal'); }} className="ie-link">Home Page</a>
          <a href="#" onClick={(e) => { e.preventDefault(); navigateTo('youtoob'); }} className="ie-link">YouBoob</a>
          <a href="#" onClick={(e) => { e.preventDefault(); navigateTo('myface'); }} className="ie-link">FaceLeak</a>
          <a href="#" onClick={(e) => { e.preventDefault(); navigateTo('megafunzone'); }} className="ie-link">MegaFunExploit</a>
        </div>
      </div>

      {/* IE Content */}
      <div className="ie-content" data-testid="ie-content">
        {renderCurrentSite()}
      </div>

      {/* Status Bar */}
      <div className="ie-status-bar">
        <span className="status-text">Done</span>
        <span className="status-zone">{PARODY_SITES[currentSite]?.url || PARODY_SITES.portal.url}</span>
        <span className="status-zone">Internet</span>
        <span className="zoom-level">100%</span>
      </div>
    </div>
  );
};

export default InternetExplorer;
